export const RECEIVABLES_KILL_SWITCH = 'qm_disable_receivables_intelligence';

export const RECEIVABLES_STATUS_OPTIONS = Object.freeze([
    'Pending Review',
    'Ready To Chase',
    'Promised To Pay',
    'Awaiting Manager Approval',
    'Awaiting Committee Approval',
    'Awaiting Completion Evidence',
    'Awaiting Photos / Report',
    'Disputed',
    'Warranty / Defect Re-attend',
    'Wrong Contact / Contact Unknown',
    'Paid / Resolved',
    'Escalated Internally',
    'Do Not Chase Yet',
]);

export const RECEIVABLES_IMPLEMENTATION_AREAS = Object.freeze([
    {
        id: 'local-case-register',
        label: 'Local case register',
        phase: 'Phase 1',
        status: 'live',
        dataStores: ['receivable_cases'],
        description: 'Static JSON/CSV imports, Xero export imports, human status, notes, next follow-up date, and priority sorting.',
        nextStep: 'Keep adding real export header presets as RJ BIRD discovers new report shapes.',
    },
    {
        id: 'xero-export-import',
        label: 'Xero export import',
        phase: 'Phase 2',
        status: 'partial',
        dataStores: ['receivable_cases'],
        description: 'Regular Xero invoice/receivables CSV exports are preprocessed into local receivable cases without OAuth or Xero writes.',
        nextStep: 'Validate against RJ BIRD’s exact Xero export once available and add saved export presets if needed.',
    },
    {
        id: 'xero-readonly-oauth',
        label: 'Xero read-only OAuth',
        phase: 'Later',
        status: 'placeholder',
        dataStores: ['receivable_sync_state', 'receivable_evidence_items'],
        description: 'Optional future OAuth-backed invoice, payment, credit-note, contact, and due-date evidence. No Xero writes.',
        nextStep: 'Only build after regular export import proves the workflow and data model.',
    },
    {
        id: 'email-readonly-ingestion',
        label: 'Email read-only ingestion',
        phase: 'Phase 2',
        status: 'partial',
        dataStores: ['receivable_evidence_items'],
        description: 'Outlook/exported email evidence for invoice numbers, SPs, work orders, approvals, disputes, and promises to pay.',
        nextStep: 'Add Outlook Graph read-only import, attachment references, duplicate/thread review, and richer quoted source snippets.',
    },
    {
        id: 'quotemaker-project-linking',
        label: 'QuoteMaker project linking',
        phase: 'Phase 1.5',
        status: 'partial',
        dataStores: ['receivable_project_links', 'projects'],
        description: 'Scored links from invoice cases to saved QuoteMaker projects using quote refs, work orders, SPs, addresses, and requester names.',
        nextStep: 'Persist accepted links, show alternates, and add conflict review for weak matches.',
    },
    {
        id: 'evidence-timeline',
        label: 'Evidence timeline',
        phase: 'Phase 1.5',
        status: 'partial',
        dataStores: ['receivable_evidence_items', 'receivable_cases'],
        description: 'Source-by-source case history combining invoice facts, system notes, scope details, manual notes, and persisted Xero/email/file evidence rows.',
        nextStep: 'Add attachment refs, redaction review, and conflict resolution for weak evidence matches.',
    },
    {
        id: 'assistant-review',
        label: 'Assistant review',
        phase: 'Phase 1',
        status: 'live-readonly',
        dataStores: ['receivable_cases', 'receivable_evidence_items'],
        description: 'Assistant can search and inspect receivable cases and explain missing evidence. It cannot send, call, or write back.',
        nextStep: 'Add selected-case context injection and citation-focused summaries once evidence rows are persisted.',
    },
    {
        id: 'human-action-register',
        label: 'Human action register',
        phase: 'Phase 2',
        status: 'scaffolded',
        dataStores: ['receivable_actions'],
        description: 'Records approved calls, emails, waits, escalations, owners, due dates, and completed outcomes.',
        nextStep: 'Add action logging, owner filters, follow-up reminders, and no-harassment contact limits.',
    },
]);

export const RECEIVABLES_ASSISTANT_CAPABILITIES = Object.freeze([
    { id: 'search-cases', label: 'Search receivable cases', state: 'enabled', tone: 'green' },
    { id: 'inspect-case', label: 'Inspect selected case evidence', state: 'enabled', tone: 'green' },
    { id: 'inspect-email-evidence', label: 'Inspect imported email evidence', state: 'enabled', tone: 'green' },
    { id: 'explain-missing', label: 'Explain missing evidence', state: 'enabled', tone: 'green' },
    { id: 'draft-wording', label: 'Draft wording for human review', state: 'placeholder', tone: 'amber' },
    { id: 'send-email', label: 'Send email automatically', state: 'blocked', tone: 'red' },
    { id: 'write-xero', label: 'Write or update Xero', state: 'blocked', tone: 'red' },
]);

const STATUS_SET = new Set(RECEIVABLES_STATUS_OPTIONS);

const AGEING_BUCKETS = Object.freeze([
    { key: 'current', label: 'Current', min: -Infinity, max: 0 },
    { key: '1-7', label: '1-7', min: 1, max: 7 },
    { key: '8-14', label: '8-14', min: 8, max: 14 },
    { key: '15-30', label: '15-30', min: 15, max: 30 },
    { key: '31-60', label: '31-60', min: 31, max: 60 },
    { key: '60+', label: '60+', min: 61, max: Infinity },
]);

export const RECEIVABLES_IMPORT_FIELD_ALIASES = Object.freeze({
    invoiceNumber: ['invoice number', 'invoice no', 'invoice no.', 'invoice #', 'number', 'invoice id', 'invoice', 'inv', 'inv no', 'invoiceNumber'],
    xeroInvoiceId: ['xero invoice id', 'xero id', 'xeroInvoiceId', 'invoiceid', 'invoice uuid'],
    amountDue: ['amount outstanding', 'outstanding amount', 'amount due', 'remaining due', 'remaining amount', 'unpaid amount', 'amount', 'due', 'balance', 'outstanding'],
    amountOriginal: ['amount original', 'original amount', 'invoice total', 'total amount', 'invoice amount', 'total', 'gross amount'],
    amountPaid: ['amount paid', 'paid amount', 'paid', 'allocated amount'],
    daysOverdue: ['days overdue', 'overdue days', 'overdue_days', 'days late', 'age', 'ageing', 'aging', 'overdue'],
    sp: ['sp', 'strata plan', 'strata_plan', 'strata plan number'],
    propertyAddress: ['property', 'property address', 'site address', 'address', 'to', 'street address'],
    street: ['street', 'street address'],
    suburb: ['suburb'],
    strataManager: ['strata manager', 'strata_manager', 'manager', 'manager name', 'contact name'],
    company: ['company', 'client company', 'requester', 'customer', 'customer name', 'contact', 'contact name', 'name', 'debtor', 'debtor name', 'invoicing details', 'invoicing_details', 'sm company', 'sm_company'],
    managerEmail: ['manager email', 'manager_email', 'email', 'email address', 'emailaddress', 'contact email', 'accounts email', 'accounts email address', 'strata contact', 'strata_contact', 'site contacts', 'site_contacts', 'suburb', 'street'],
    siteContact: ['site contact', 'site contacts', 'site_contact', 'site_contacts', 'tenant contact'],
    invoiceDate: ['invoice date', 'inv date', 'inv_date', 'date'],
    dueDate: ['due date', 'due_date', 'payment due date', 'date due'],
    workOrder: ['work order', 'work_order', 'work order number', 'order number', 'invoice reference', 'your reference', 'ref', 'reference'],
    quoteRef: ['quote ref', 'quote_ref', 'quote reference', 'quotation ref', 'quote number'],
    scopeDetails: ['scope details', 'scope_details', 'scope', 'description', 'job description'],
    sysNote: ['sys note', 'sys_note', 'verified note / status', 'verified note status', 'last known corr', 'last known correspondence', 'system note', 'recovery note'],
    verificationStatus: ['verification status', 'confidence', 'source confidence'],
    manualNotes: ['investigation notes', 'manual notes', 'action notes', 'recovery notes', 'notes'],
    nextActionDate: ['next action date', 'next_action_date', 'next follow up', 'next follow-up'],
    status: ['status', 'invoice status', 'xero status', 'recovery status', 'case status'],
});

export const RECEIVABLES_IMPORT_TEMPLATE_HEADERS = Object.freeze([
    'invoice',
    'amount',
    'days_overdue',
    'sp',
    'property',
    'strata_manager',
    'company',
    'manager_email',
    'site_contact',
    'work_order',
    'quote_ref',
    'scope_details',
    'sys_note',
    'verification_status',
    'investigation_notes',
    'status',
    'next_action_date',
    'invoice_date',
    'due_date',
    'xero_invoice_id',
]);

export const RECEIVABLES_IMPORT_PREVIEW_FIELDS = Object.freeze([
    { id: 'invoiceNumber', label: 'Invoice', required: true },
    { id: 'amountDue', label: 'Amount due', required: true },
    { id: 'daysOverdue', label: 'Ageing' },
    { id: 'sp', label: 'SP' },
    { id: 'propertyAddress', label: 'Property' },
    { id: 'strataManager', label: 'Manager' },
    { id: 'company', label: 'Company' },
    { id: 'managerEmail', label: 'Manager email' },
    { id: 'workOrder', label: 'Work order' },
    { id: 'quoteRef', label: 'Quote ref' },
    { id: 'sysNote', label: 'Status note' },
]);

function isBlank(value) {
    if (value == null) return true;
    const s = String(value).trim();
    return !s || /^n\/?a$/i.test(s) || /^unknown$/i.test(s) || /^null$/i.test(s);
}

function cleanString(value) {
    return isBlank(value) ? '' : String(value).trim();
}

function normalizeHeader(value) {
    return cleanString(value)
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[._-]+/g, ' ')
        .trim();
}

function pick(record, aliases) {
    if (!record || typeof record !== 'object') return '';
    const lookup = new Map();
    for (const [key, value] of Object.entries(record)) {
        lookup.set(normalizeHeader(key), value);
    }
    for (const alias of aliases) {
        const value = lookup.get(normalizeHeader(alias));
        if (!isBlank(value)) return value;
    }
    return '';
}

function pickField(record, field) {
    return pick(record, RECEIVABLES_IMPORT_FIELD_ALIASES[field] || []);
}

function aliasMatchesField(header, field) {
    const normalized = normalizeHeader(header);
    return (RECEIVABLES_IMPORT_FIELD_ALIASES[field] || []).some(alias => normalizeHeader(alias) === normalized);
}

function fieldsForHeader(header) {
    return Object.keys(RECEIVABLES_IMPORT_FIELD_ALIASES).filter(field => aliasMatchesField(header, field));
}

function firstNonBlank(...values) {
    for (const value of values) {
        if (!isBlank(value)) return value;
    }
    return '';
}

function extractEmail(value) {
    const raw = cleanString(value);
    if (!raw) return '';
    const match = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    return match ? match[0].trim() : '';
}

function stripEmail(value) {
    return cleanString(value).replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/ig, '').replace(/[<>()]/g, ' ').replace(/\s+/g, ' ').trim();
}

function isStrataPlanOnly(value) {
    return /^strata\s+plan\s+\S+$/i.test(cleanString(value));
}

function cleanAddressPart(value) {
    const part = cleanString(value);
    if (!part || extractEmail(part)) return '';
    if (/^(strata\s+plan|sp)\s+\S+$/i.test(part)) return '';
    return part;
}

function extractStrataPlan(value) {
    const text = cleanString(value);
    if (!text) return '';
    const match = text.match(/\b(?:SP|Strata\s+Plan)\s*#?\s*:?\s*([0-9]{3,})\b/i);
    return match ? match[1] : '';
}

function resolvePropertyAddress(input) {
    const explicit = cleanString(input.propertyAddress || input.property || input.address || pickField(input, 'propertyAddress'));
    const street = cleanAddressPart(input.street || pickField(input, 'street'));
    const suburb = cleanAddressPart(input.suburb || pickField(input, 'suburb'));

    if (explicit && !isStrataPlanOnly(explicit)) return explicit;
    if (street && suburb && street.toLowerCase() !== suburb.toLowerCase()) return `${street}, ${suburb}`;
    if (street) return street;
    if (suburb) return suburb;
    return explicit;
}

function looksLikeCompany(value) {
    const text = cleanString(value);
    if (!text || extractEmail(text)) return false;
    if (/(\$|%|\bcontract\b|\bdeposit\b|\bfee\b|\breceived\b|\binvoice\b|\bquote\b|\bstrata plan\b)/i.test(text)) return false;
    return true;
}

function resolveCompany(input) {
    const candidates = [
        input.company,
        pickField(input, 'company'),
        pick(input, ['company', 'client company', 'requester']),
        input.invoicingDetails,
        input.invoicing_details,
        pick(input, ['invoicing details', 'invoicing_details']),
        input.sm_company,
        pick(input, ['sm company', 'sm_company']),
    ];
    return cleanString(candidates.find(looksLikeCompany) || firstNonBlank(...candidates));
}

function resolveManagerEmail(input) {
    const candidates = [
        input.managerEmail,
        input.manager_email,
        pickField(input, 'managerEmail'),
        pick(input, ['manager email', 'manager_email', 'email', 'accounts email']),
        input.strata_contact,
        pick(input, ['strata contact', 'strata_contact']),
        input.site_contacts,
        input.siteContact,
        pickField(input, 'siteContact'),
        input.suburb,
        pickField(input, 'suburb'),
        input.street,
        pickField(input, 'street'),
    ];
    for (const candidate of candidates) {
        const email = extractEmail(candidate);
        if (email) return email;
    }
    return '';
}

export function parseReceivablesAmount(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (isBlank(value)) return 0;
    const cleaned = String(value)
        .replace(/\(([^)]+)\)/, '-$1')
        .replace(/[^0-9.-]/g, '');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
}

function parseInteger(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
    if (isBlank(value)) return 0;
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
}

function parseOptionalInteger(value) {
    if (isBlank(value)) return null;
    return parseInteger(value);
}

export function normalizeReceivablesDate(value) {
    if (isBlank(value)) return '';
    const raw = cleanString(value);
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

    const au = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (au) {
        const day = au[1].padStart(2, '0');
        const month = au[2].padStart(2, '0');
        const year = au[3].length === 2 ? `20${au[3]}` : au[3];
        return `${year}-${month}-${day}`;
    }

    const monthName = raw.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/);
    if (monthName) {
        const months = {
            jan: '01', january: '01',
            feb: '02', february: '02',
            mar: '03', march: '03',
            apr: '04', april: '04',
            may: '05',
            jun: '06', june: '06',
            jul: '07', july: '07',
            aug: '08', august: '08',
            sep: '09', sept: '09', september: '09',
            oct: '10', october: '10',
            nov: '11', november: '11',
            dec: '12', december: '12',
        };
        const month = months[monthName[2].toLowerCase()];
        if (month) return `${monthName[3]}-${month}-${monthName[1].padStart(2, '0')}`;
    }

    const parsed = Date.parse(raw);
    if (!Number.isFinite(parsed)) return '';
    return new Date(parsed).toISOString().slice(0, 10);
}

function inferDaysOverdue(dueDateIso, today = new Date()) {
    if (!dueDateIso) return 0;
    const due = Date.parse(`${dueDateIso}T00:00:00`);
    const now = today instanceof Date ? today : new Date(today);
    if (!Number.isFinite(due) || Number.isNaN(now.getTime())) return 0;
    const utcToday = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.floor((utcToday - due) / 86400000);
    return Math.max(0, days);
}

function formatSourceLabel(source = {}) {
    if (source.profileLabel) return source.profileLabel;
    if (source.filename) return source.filename;
    if (source.kind) return source.kind;
    return 'static-import';
}

function evidenceRef(type, label, excerpt) {
    return {
        id: `${type}:${String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'source'}`,
        type,
        label: cleanString(label),
        excerpt: cleanString(excerpt),
    };
}

function buildEvidenceRefs(caseRecord) {
    const refs = [];
    if (caseRecord.invoiceNumber) refs.push(evidenceRef('invoice', caseRecord.invoiceNumber, `Invoice ${caseRecord.invoiceNumber}`));
    if (caseRecord.source?.filename) refs.push(evidenceRef('import', caseRecord.source.filename, 'Imported debtor register row'));
    if (caseRecord.sysNote) refs.push(evidenceRef('system-note', 'Imported status note', caseRecord.sysNote));
    if (caseRecord.scopeDetails) refs.push(evidenceRef('scope', caseRecord.quoteRef || caseRecord.workOrder || 'Scope details', caseRecord.scopeDetails));
    if (caseRecord.manualNotes) refs.push(evidenceRef('manual-note', 'Investigation notes', caseRecord.manualNotes));
    if (caseRecord.managerEmail || caseRecord.strataManager || caseRecord.company) {
        refs.push(evidenceRef('contact', caseRecord.managerEmail || caseRecord.strataManager || caseRecord.company, [
            caseRecord.company,
            caseRecord.strataManager,
            caseRecord.managerEmail,
        ].filter(Boolean).join(' / ')));
    }
    return refs;
}

function actionForStatus(status, caseRecord) {
    switch (status) {
        case 'Paid / Resolved':
            return 'Close the case after confirming the payment in Xero.';
        case 'Disputed':
            return 'Review the dispute trail and clarify the job position before any payment reminder.';
        case 'Warranty / Defect Re-attend':
            return 'Resolve the warranty or re-attend position before chasing payment.';
        case 'Awaiting Photos / Report':
        case 'Awaiting Completion Evidence':
            return 'Find the completion evidence or request it before any overdue reminder.';
        case 'Awaiting Manager Approval':
        case 'Awaiting Committee Approval':
            return 'Follow up as an approval-status check, not a demand for payment.';
        case 'Promised To Pay':
            return 'Wait until the promised payment date, then review if Xero still shows outstanding.';
        case 'Wrong Contact / Contact Unknown':
            return 'Find the right strata manager or accounts contact before contacting anyone.';
        case 'Ready To Chase':
            return caseRecord.managerEmail
                ? `Draft a polite human-reviewed reminder to ${caseRecord.managerEmail}.`
                : 'Draft a polite reminder only after confirming the right contact.';
        case 'Do Not Chase Yet':
            return 'Hold contact until the scheduled work, delivery, or evidence milestone has passed.';
        case 'Escalated Internally':
            return 'Keep internal owner notes current and avoid client contact until the escalation is resolved.';
        default:
            return 'Investigate Xero, QuoteMaker, email, and folder evidence before contact.';
    }
}

function inferStatus(caseRecord) {
    const provided = cleanString(caseRecord.status);
    if (STATUS_SET.has(provided) && provided !== 'Pending Review') {
        return {
            status: provided,
            reason: caseRecord.statusReason || `Imported/manual status is ${provided}.`,
            confidence: provided === 'Disputed' || provided === 'Warranty / Defect Re-attend' ? 'high' : 'medium',
        };
    }

    const haystack = [
        caseRecord.status,
        caseRecord.statusReason,
        caseRecord.sysNote,
        caseRecord.manualNotes,
        caseRecord.scopeDetails,
        caseRecord.lastKnownCorrespondence,
    ].map(v => cleanString(v).toLowerCase()).join(' ');

    if ((caseRecord.amountDue <= 0 && caseRecord.amountOriginal > 0) || /\bpaid\b|\bresolved\b|\bclosed\b/.test(haystack)) {
        return { status: 'Paid / Resolved', reason: 'The imported facts indicate no active recovery is needed.', confidence: 'medium' };
    }
    if (/\bdisput|premature|not approved|incorrect|wrong invoice|query|hold\b/.test(haystack)) {
        return { status: 'Disputed', reason: 'The imported notes mention a dispute, premature invoice, hold, or approval issue.', confidence: 'high' };
    }
    if (/\bwarranty\b|defect|re-?attend|reattend/.test(haystack)) {
        return { status: 'Warranty / Defect Re-attend', reason: 'The imported notes flag warranty or defect re-attend work.', confidence: 'high' };
    }
    if (/photo|report|completion evidence|evidence required|requires evidence/.test(haystack)) {
        return { status: 'Awaiting Photos / Report', reason: 'The imported notes indicate completion photos, a report, or other evidence is missing.', confidence: 'high' };
    }
    if (/committee/.test(haystack)) {
        return { status: 'Awaiting Committee Approval', reason: 'The imported notes indicate committee approval is pending.', confidence: 'medium' };
    }
    if (/manager approval|approval pending|pending approval/.test(haystack)) {
        return { status: 'Awaiting Manager Approval', reason: 'The imported notes indicate manager approval is pending.', confidence: 'medium' };
    }
    if (/promised|promise to pay|will pay|payment run|pay run/.test(haystack)) {
        return { status: 'Promised To Pay', reason: 'The imported notes indicate a promised payment or payment run.', confidence: 'medium' };
    }
    if (/scheduled|lead-?time|pending delivery|window delivery|work getting done|getting done atm|work in progress/.test(haystack)) {
        return { status: 'Do Not Chase Yet', reason: 'The imported notes indicate the work, delivery, or completion milestone is not ready to chase.', confidence: 'medium' };
    }
    if (!caseRecord.managerEmail && !caseRecord.strataManager && !caseRecord.company) {
        return { status: 'Wrong Contact / Contact Unknown', reason: 'The imported row does not identify a reliable recovery contact.', confidence: 'medium' };
    }
    if (caseRecord.daysOverdue > 0) {
        return { status: 'Ready To Chase', reason: 'The invoice is overdue and no dispute/evidence blocker was detected in the imported row.', confidence: 'medium' };
    }
    return { status: 'Pending Review', reason: 'No overdue or blocker signal is strong enough yet.', confidence: 'low' };
}

export function normalizeReceivablesCase(input = {}, options = {}) {
    const source = {
        kind: options.sourceKind || input.sourceKind || input.source?.kind || 'static-import',
        filename: options.filename || input.sourceFilename || input.source?.filename || '',
        profileId: options.sourceProfile?.id || input.sourceProfileId || input.source?.profileId || '',
        profileLabel: options.sourceProfile?.label || input.sourceProfileLabel || input.source?.profileLabel || '',
        importedAt: options.importedAt || input.source?.importedAt || new Date().toISOString(),
    };

    const invoiceNumber = cleanString(input.invoiceNumber || input.invoice || input.inv || pickField(input, 'invoiceNumber'));
    const invoiceDate = cleanString(input.invoiceDate || input.invDate || input.inv_date || pickField(input, 'invoiceDate'));
    const dueDate = cleanString(input.dueDate || input.due_date || pickField(input, 'dueDate'));
    const dueDateIso = normalizeReceivablesDate(dueDate);
    const explicitDaysOverdue = parseOptionalInteger(input.daysOverdue ?? input.days_overdue ?? input.overdue_days ?? input.overdue ?? pickField(input, 'daysOverdue'));
    const sourceReference = firstNonBlank(input.reference, input.ref, pick(input, ['reference', 'invoice reference', 'your reference', 'ref']));
    const amount = parseReceivablesAmount(firstNonBlank(
        input.amountDue,
        input.amountOutstanding,
        input.amount,
        input.due,
        pickField(input, 'amountDue')
    ));
    const amountOriginal = parseReceivablesAmount(firstNonBlank(input.amountOriginal, input.originalAmount, input.total, pickField(input, 'amountOriginal'), amount));
    const strataContactName = stripEmail(input.strataContact || input.strata_contact || pick(input, ['strata contact', 'strata_contact']));

    const base = {
        id: cleanString(input.id) || invoiceNumber || `receivable_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        invoiceNumber,
        xeroInvoiceId: cleanString(input.xeroInvoiceId || input.xero_invoice_id || pickField(input, 'xeroInvoiceId')),
        amountDue: amount,
        amountOriginal: amountOriginal || amount,
        amountPaid: parseReceivablesAmount(input.amountPaid ?? pickField(input, 'amountPaid')),
        daysOverdue: explicitDaysOverdue ?? inferDaysOverdue(dueDateIso, options.today),
        sp: cleanString(input.sp || input.strataPlan || input.strata_plan || input['Strata Plan'] || pickField(input, 'sp') || extractStrataPlan(sourceReference)).replace(/^SP\s+/i, ''),
        propertyAddress: resolvePropertyAddress(input),
        strataManager: cleanString(input.strataManager || input.strata_manager || input.manager || pickField(input, 'strataManager') || strataContactName),
        company: resolveCompany(input),
        managerEmail: resolveManagerEmail(input),
        siteContact: cleanString(input.siteContact || input.site_contact || input.site_contacts || pickField(input, 'siteContact')),
        invoiceDate,
        dueDate,
        workOrder: cleanString(input.workOrder || input.work_order || input.ref || pickField(input, 'workOrder')),
        quoteRef: cleanString(input.quoteRef || input.quote_ref || pickField(input, 'quoteRef')),
        scopeDetails: cleanString(input.scopeDetails || input.scope_details || pickField(input, 'scopeDetails')),
        sysNote: cleanString(input.sysNote || input.sys_note || input.verifiedNote || input.lastKnownCorr || pickField(input, 'sysNote')),
        verificationStatus: cleanString(input.verificationStatus || pickField(input, 'verificationStatus')),
        manualNotes: cleanString(input.manualNotes || input.investigation_notes || input.investigationNotes || pickField(input, 'manualNotes')),
        nextActionDate: normalizeReceivablesDate(input.nextActionDate || input.next_action_date || pickField(input, 'nextActionDate')),
        status: cleanString(input.status || pickField(input, 'status')),
        statusReason: cleanString(input.statusReason || input.status_reason),
        lastReviewedAt: cleanString(input.lastReviewedAt || input.last_reviewed_at),
        reviewedBy: cleanString(input.reviewedBy || input.reviewed_by),
        source,
        raw: options.keepRaw === false ? undefined : input,
        created_at: input.created_at || new Date().toISOString(),
        updated_at: input.updated_at || new Date().toISOString(),
    };

    base.invoiceDateIso = normalizeReceivablesDate(base.invoiceDate);
    base.dueDateIso = dueDateIso;

    const inferred = inferStatus(base);
    base.status = STATUS_SET.has(base.status) && base.status !== 'Pending Review' ? base.status : inferred.status;
    base.statusReason = base.statusReason || inferred.reason;
    base.confidence = cleanString(input.confidence || input.statusConfidence || input.status_confidence) || inferred.confidence;
    base.recommendedAction = cleanString(input.recommendedAction || input.recommended_action) || actionForStatus(base.status, base);
    base.evidence = Array.isArray(input.evidence) ? input.evidence : buildEvidenceRefs(base);
    base.missingEvidence = buildMissingEvidence(base);
    base.doNotContact = ['Disputed', 'Warranty / Defect Re-attend', 'Awaiting Photos / Report', 'Awaiting Completion Evidence', 'Do Not Chase Yet'].includes(base.status);

    return base;
}

function buildMissingEvidence(caseRecord) {
    const missing = [];
    const hasXeroExportSource = /^xero-/.test(cleanString(caseRecord.source?.profileId));
    if (!caseRecord.xeroInvoiceId && !hasXeroExportSource) missing.push('Xero invoice ID');
    if (!caseRecord.managerEmail && !caseRecord.strataManager) missing.push('recovery contact');
    if (!caseRecord.quoteRef && !caseRecord.workOrder) missing.push('QuoteMaker quote or work-order match');
    if (!caseRecord.manualNotes && ['Disputed', 'Do Not Chase Yet', 'Warranty / Defect Re-attend'].includes(caseRecord.status)) {
        missing.push('human investigation notes');
    }
    return missing;
}

function parseReceivablesCsvRows(text) {
    const rows = [];
    let row = [];
    let cell = '';
    let inQuotes = false;

    const pushCell = () => {
        row.push(cell);
        cell = '';
    };
    const pushRow = () => {
        pushCell();
        if (row.some(v => cleanString(v))) rows.push(row);
        row = [];
    };

    const input = String(text || '').replace(/^\uFEFF/, '');
    for (let i = 0; i < input.length; i += 1) {
        const ch = input[i];
        const next = input[i + 1];
        if (inQuotes) {
            if (ch === '"' && next === '"') {
                cell += '"';
                i += 1;
            } else if (ch === '"') {
                inQuotes = false;
            } else {
                cell += ch;
            }
            continue;
        }
        if (ch === '"') {
            inQuotes = true;
        } else if (ch === ',') {
            pushCell();
        } else if (ch === '\n') {
            pushRow();
        } else if (ch !== '\r') {
            cell += ch;
        }
    }
    if (cell || row.length) pushRow();
    return rows;
}

export function parseReceivablesCsvTable(text) {
    const rows = parseReceivablesCsvRows(text);
    if (rows.length === 0) {
        return { rawHeaders: [], headers: [], rows: [] };
    }

    const rawHeaders = rows[0].map(value => cleanString(value));
    const headers = rawHeaders.map(normalizeHeader);
    const parsedRows = rows.slice(1).map(values => {
        const out = {};
        headers.forEach((header, index) => {
            out[header || `column_${index + 1}`] = values[index] ?? '';
        });
        return out;
    });
    return { rawHeaders, headers, rows: parsedRows };
}

export function parseReceivablesCsv(text) {
    return parseReceivablesCsvTable(text).rows;
}

function parseReceivablesSourceRows(text, filename = 'import') {
    const sourceKind = filename.toLowerCase().endsWith('.json') ? 'json-import' : 'csv-import';
    const trimmed = String(text || '').trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        const parsed = JSON.parse(trimmed);
        const rows = Array.isArray(parsed) ? parsed : (Array.isArray(parsed.cases) ? parsed.cases : [parsed]);
        const headers = [...new Set(rows.flatMap(row => Object.keys(row || {})))];
        return {
            sourceKind: 'json-import',
            rawHeaders: headers,
            headers: headers.map(normalizeHeader),
            rows,
        };
    }
    const table = parseReceivablesCsvTable(trimmed);
    return {
        sourceKind,
        ...table,
    };
}

function hasHeader(headerSet, aliases = []) {
    return aliases.some(alias => headerSet.has(normalizeHeader(alias)));
}

export function identifyReceivablesImportSource(filename = 'import', rawHeaders = []) {
    const lowerName = String(filename || '').toLowerCase();
    const headerSet = new Set(rawHeaders.map(normalizeHeader));
    const looksLikeXeroInvoiceExport = lowerName.includes('xero')
        || (
            hasHeader(headerSet, ['invoice number', 'invoice no', 'invoice #', 'number'])
            && hasHeader(headerSet, ['contact', 'contact name', 'customer', 'name'])
            && hasHeader(headerSet, ['due date', 'date due'])
            && hasHeader(headerSet, ['total', 'total amount', 'paid', 'amount paid', 'due', 'amount due', 'amount outstanding'])
        )
        || (
            hasHeader(headerSet, ['aged receivables', 'aged receivable'])
            && hasHeader(headerSet, ['invoice number', 'invoice no', 'invoice #'])
        );
    if (looksLikeXeroInvoiceExport) {
        return {
            id: 'xero-receivables-export',
            label: 'Xero receivables export',
            confidence: lowerName.includes('xero') ? 'high' : 'medium',
        };
    }
    if (lowerName.includes('full_debtors_list') || (headerSet.has('inv') && headerSet.has('ref') && headerSet.has('overdue days'))) {
        return {
            id: 'minerva-full-debtors-list',
            label: 'Full debtors list',
            confidence: 'high',
        };
    }
    if (lowerName.includes('recovery_action_dashboard') || (headerSet.has('last known corr') && headerSet.has('verification status'))) {
        return {
            id: 'minerva-recovery-action-dashboard',
            label: 'Recovery action dashboard',
            confidence: 'high',
        };
    }
    if (lowerName.includes('definitive_recovery_truth') || headerSet.has('verified note / status')) {
        return {
            id: 'minerva-definitive-recovery-truth',
            label: 'Recovery status register',
            confidence: 'medium',
        };
    }
    if (lowerName.endsWith('.json')) {
        return {
            id: 'receivables-json',
            label: 'Receivables JSON',
            confidence: 'medium',
        };
    }
    return {
        id: 'generic-receivables',
        label: 'Generic receivables import',
        confidence: 'low',
    };
}

function buildReceivablesColumnMappings(rawHeaders = []) {
    return rawHeaders
        .map(header => ({
            source: cleanString(header),
            fields: fieldsForHeader(header),
        }))
        .filter(mapping => mapping.source);
}

function fieldValuePresent(caseRecord, field) {
    if (!caseRecord) return false;
    if (field === 'amountDue') return Number(caseRecord.amountDue) > 0;
    if (field === 'daysOverdue') return Number.isFinite(Number(caseRecord.daysOverdue));
    return !isBlank(caseRecord[field]);
}

export function buildReceivablesImportPreview(text, filename = 'import', options = {}) {
    const parsed = parseReceivablesSourceRows(text, filename);
    const source = identifyReceivablesImportSource(filename, parsed.rawHeaders);
    const cases = parsed.rows
        .map(row => normalizeReceivablesCase(row, { sourceKind: parsed.sourceKind, filename, sourceProfile: source }))
        .filter(row => row.invoiceNumber || row.amountDue || row.propertyAddress || row.company);
    const mappings = buildReceivablesColumnMappings(parsed.rawHeaders);
    const mappedFields = new Set(mappings.flatMap(mapping => mapping.fields));
    const unmappedHeaders = mappings
        .filter(mapping => mapping.fields.length === 0)
        .map(mapping => mapping.source);
    const fieldCoverage = RECEIVABLES_IMPORT_PREVIEW_FIELDS.map(field => {
        const count = cases.filter(row => fieldValuePresent(row, field.id)).length;
        return {
            ...field,
            count,
            percent: cases.length > 0 ? Math.round((count / cases.length) * 100) : 0,
            mapped: mappedFields.has(field.id),
        };
    });
    const warnings = [];

    if (parsed.rows.length === 0) {
        warnings.push({ severity: 'danger', message: 'No rows were found after preprocessing.' });
    }
    if (cases.length === 0 && parsed.rows.length > 0) {
        warnings.push({ severity: 'danger', message: 'Rows were found, but none contain enough invoice, amount, property, or company data to import.' });
    }
    for (const field of fieldCoverage.filter(item => item.required && item.percent < 100)) {
        warnings.push({ severity: field.percent === 0 ? 'danger' : 'warning', message: `${field.label} is present on ${field.count} of ${cases.length} importable row${cases.length === 1 ? '' : 's'}.` });
    }
    const emailCoverage = fieldCoverage.find(item => item.id === 'managerEmail');
    if (cases.length > 0 && emailCoverage && emailCoverage.percent < 50) {
        warnings.push({ severity: 'warning', message: `Only ${emailCoverage.count} of ${cases.length} rows have a manager email. Treat reminders as blocked until the right contact is confirmed.` });
    }
    const propertyCoverage = fieldCoverage.find(item => item.id === 'propertyAddress');
    if (cases.length > 0 && propertyCoverage && propertyCoverage.percent < 70) {
        warnings.push({ severity: 'warning', message: `Only ${propertyCoverage.count} of ${cases.length} rows have a usable property address. Project matching will be weaker.` });
    }
    if (unmappedHeaders.length > 0) {
        warnings.push({ severity: 'info', message: `${unmappedHeaders.length} source column${unmappedHeaders.length === 1 ? '' : 's'} are not used yet: ${unmappedHeaders.slice(0, 6).join(', ')}${unmappedHeaders.length > 6 ? '...' : ''}.` });
    }
    if (options.preprocessor?.warnings?.length) {
        warnings.push(...options.preprocessor.warnings.map(message => ({ severity: 'info', message })));
    }
    if (options.preprocessor?.errors?.length) {
        warnings.push(...options.preprocessor.errors.map(message => ({ severity: 'warning', message })));
    }

    return {
        filename,
        sourceKind: parsed.sourceKind,
        source,
        rowsRead: parsed.rows.length,
        importableCount: cases.length,
        skippedCount: Math.max(0, parsed.rows.length - cases.length),
        rawHeaders: parsed.rawHeaders,
        columnMappings: mappings,
        unmappedHeaders,
        fieldCoverage,
        warnings,
        sampleCases: cases.slice(0, 5).map(row => ({
            invoiceNumber: row.invoiceNumber,
            amountDue: row.amountDue,
            daysOverdue: row.daysOverdue,
            propertyAddress: row.propertyAddress,
            company: row.company,
            managerEmail: row.managerEmail,
            status: row.status,
        })),
    };
}

export function parseReceivablesImport(text, filename = 'import') {
    const parsed = parseReceivablesSourceRows(text, filename);
    const source = identifyReceivablesImportSource(filename, parsed.rawHeaders);
    return parsed.rows
        .map(row => normalizeReceivablesCase(row, { sourceKind: parsed.sourceKind, filename, sourceProfile: source }))
        .filter(row => row.invoiceNumber || row.amountDue || row.propertyAddress || row.company);
}

function csvEscape(value) {
    if (value == null) return '';
    const text = String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function receivablesCaseToTemplateRow(caseRecord = {}) {
    const c = normalizeReceivablesCase(caseRecord, { keepRaw: false });
    return {
        invoice: c.invoiceNumber,
        amount: c.amountDue || '',
        days_overdue: Number.isFinite(Number(c.daysOverdue)) ? c.daysOverdue : '',
        sp: c.sp,
        property: c.propertyAddress,
        strata_manager: c.strataManager,
        company: c.company,
        manager_email: c.managerEmail,
        site_contact: c.siteContact,
        work_order: c.workOrder,
        quote_ref: c.quoteRef,
        scope_details: c.scopeDetails,
        sys_note: c.sysNote,
        verification_status: c.verificationStatus,
        investigation_notes: c.manualNotes,
        status: c.status,
        next_action_date: c.nextActionDate,
        invoice_date: c.invoiceDateIso || c.invoiceDate,
        due_date: c.dueDateIso || c.dueDate,
        xero_invoice_id: c.xeroInvoiceId,
    };
}

export function serializeReceivablesImportTemplateCsv(cases = []) {
    const rows = Array.isArray(cases) ? cases : [];
    const header = RECEIVABLES_IMPORT_TEMPLATE_HEADERS.join(',');
    const body = rows.map(caseRecord => {
        const row = receivablesCaseToTemplateRow(caseRecord);
        return RECEIVABLES_IMPORT_TEMPLATE_HEADERS.map(headerName => csvEscape(row[headerName])).join(',');
    });
    return [header, ...body].join('\n');
}

export function mergeReceivablesCases(existingCases = [], importedCases = []) {
    const byId = new Map();
    for (const existing of existingCases) {
        const normalized = normalizeReceivablesCase(existing, { keepRaw: false });
        byId.set(normalized.id, normalized);
        if (normalized.invoiceNumber) byId.set(`invoice:${normalized.invoiceNumber.toLowerCase()}`, normalized);
    }

    let created = 0;
    let updated = 0;
    const mergedByPrimaryId = new Map();

    for (const incomingRaw of importedCases) {
        const incoming = normalizeReceivablesCase(incomingRaw, { keepRaw: false });
        const existing = byId.get(incoming.id) || (incoming.invoiceNumber ? byId.get(`invoice:${incoming.invoiceNumber.toLowerCase()}`) : null);
        if (existing) {
            updated += 1;
            const next = {
                ...incoming,
                id: existing.id,
                created_at: existing.created_at || incoming.created_at,
                manualNotes: existing.manualNotes || incoming.manualNotes,
                status: existing.reviewedBy === 'human' && existing.status ? existing.status : incoming.status,
                statusReason: existing.reviewedBy === 'human' && existing.statusReason ? existing.statusReason : incoming.statusReason,
                nextActionDate: existing.nextActionDate || incoming.nextActionDate,
                reviewedBy: existing.reviewedBy || incoming.reviewedBy,
                lastReviewedAt: existing.lastReviewedAt || incoming.lastReviewedAt,
                updated_at: new Date().toISOString(),
            };
            next.recommendedAction = actionForStatus(next.status, next);
            next.missingEvidence = buildMissingEvidence(next);
            next.evidence = buildEvidenceRefs(next);
            mergedByPrimaryId.set(next.id, next);
        } else {
            created += 1;
            mergedByPrimaryId.set(incoming.id, incoming);
        }
    }

    for (const existing of existingCases) {
        if (!mergedByPrimaryId.has(existing.id)) mergedByPrimaryId.set(existing.id, existing);
    }

    const cases = Array.from(mergedByPrimaryId.values()).sort(compareCasePriority);
    return { cases, created, updated, imported: importedCases.length };
}

export function compareCasePriority(a, b) {
    const aPaid = a.status === 'Paid / Resolved';
    const bPaid = b.status === 'Paid / Resolved';
    if (aPaid !== bPaid) return aPaid ? 1 : -1;
    const aBlocker = a.doNotContact ? 1 : 0;
    const bBlocker = b.doNotContact ? 1 : 0;
    if (aBlocker !== bBlocker) return aBlocker - bBlocker;
    const overdue = (b.daysOverdue || 0) - (a.daysOverdue || 0);
    if (overdue) return overdue;
    return (b.amountDue || 0) - (a.amountDue || 0);
}

export function buildReceivablesOverview(cases = []) {
    const normalized = cases.map(c => normalizeReceivablesCase(c, { keepRaw: false }));
    const open = normalized.filter(c => c.status !== 'Paid / Resolved');
    const totalOutstanding = open.reduce((sum, c) => sum + (c.amountDue || 0), 0);
    const totalOverdue = open.filter(c => c.daysOverdue > 0).reduce((sum, c) => sum + (c.amountDue || 0), 0);
    const statusTotals = {};
    const ageing = {};
    for (const bucket of AGEING_BUCKETS) {
        ageing[bucket.key] = { ...bucket, count: 0, amount: 0 };
    }
    for (const c of normalized) {
        statusTotals[c.status] = (statusTotals[c.status] || 0) + 1;
        const bucket = AGEING_BUCKETS.find(b => c.daysOverdue >= b.min && c.daysOverdue <= b.max) || AGEING_BUCKETS[0];
        ageing[bucket.key].count += 1;
        ageing[bucket.key].amount += c.amountDue || 0;
    }
    const priorityQueue = open
        .filter(c => c.daysOverdue > 0 || c.status !== 'Pending Review')
        .sort(compareCasePriority)
        .slice(0, 10);
    return {
        totalCases: normalized.length,
        openCases: open.length,
        totalOutstanding,
        totalOverdue,
        disputedAmount: open
            .filter(c => c.status === 'Disputed' || c.status === 'Warranty / Defect Re-attend')
            .reduce((sum, c) => sum + (c.amountDue || 0), 0),
        promisedAmount: open
            .filter(c => c.status === 'Promised To Pay')
            .reduce((sum, c) => sum + (c.amountDue || 0), 0),
        statusTotals,
        ageing,
        priorityQueue,
    };
}

export function buildReceivablesEvidenceTimeline(caseRecord = {}, evidenceItems = []) {
    const c = normalizeReceivablesCase(caseRecord, { keepRaw: false });
    const timeline = [];
    if (c.invoiceDate || c.invoiceDateIso) {
        timeline.push({
            id: `${c.id}:invoice-date`,
            date: c.invoiceDateIso || c.invoiceDate,
            type: 'invoice',
            title: `Invoice ${c.invoiceNumber || ''}`.trim(),
            detail: `${formatCurrency(c.amountDue)} outstanding${c.dueDate ? `, due ${c.dueDate}` : ''}`,
        });
    }
    if (c.sysNote) {
        timeline.push({
            id: `${c.id}:system-note`,
            date: c.source?.importedAt || c.updated_at,
            type: 'system-note',
            title: 'Imported status note',
            detail: c.sysNote,
        });
    }
    if (c.scopeDetails) {
        timeline.push({
            id: `${c.id}:scope`,
            date: c.source?.importedAt || c.updated_at,
            type: 'scope',
            title: c.quoteRef || c.workOrder || 'Scope details',
            detail: c.scopeDetails,
        });
    }
    if (c.manualNotes) {
        timeline.push({
            id: `${c.id}:manual`,
            date: c.lastReviewedAt || c.updated_at,
            type: 'manual-note',
            title: 'Investigation notes',
            detail: c.manualNotes,
        });
    }
    for (const item of Array.isArray(evidenceItems) ? evidenceItems : []) {
        if (!item) continue;
        timeline.push({
            id: item.id || `${c.id}:${item.sourceType || 'evidence'}:${item.sourceId || item.sourceLabel || timeline.length}`,
            date: item.occurredAt || item.importedAt || c.updated_at,
            type: item.sourceType || 'evidence',
            title: item.subject || item.sourceLabel || item.filename || 'Imported evidence',
            detail: item.excerpt || item.bodyPreview || item.detail || item.summary || 'Evidence imported for this receivables case.',
            sourcePath: item.sourcePath || item.filename || '',
            matchScore: item.matchScore || null,
        });
    }
    return timeline.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

export function buildReceivablesSourceCoverage(caseRecord = {}, options = {}) {
    if (!caseRecord) return [];
    const c = normalizeReceivablesCase(caseRecord, { keepRaw: false });
    const projectMatches = Array.isArray(options.projectMatches) ? options.projectMatches : [];
    const evidenceItems = Array.isArray(options.evidenceItems) ? options.evidenceItems : [];
    const bestProjectMatch = projectMatches[0] || null;
    const bestProjectScore = Number(bestProjectMatch?.match?.score) || 0;
    const evidenceTypes = new Set([
        ...(Array.isArray(c.evidence) ? c.evidence : []).map(item => item?.type).filter(Boolean),
        ...evidenceItems.map(item => item?.sourceType || item?.type).filter(Boolean),
    ]);
    const emailEvidenceCount = evidenceItems.filter(item => (item?.sourceType || item?.type) === 'email').length;
    const hasEmailEvidence = evidenceTypes.has('email') || emailEvidenceCount > 0 || Boolean(c.emailThreadId || c.lastKnownCorrespondence);
    const hasManualReview = Boolean(c.manualNotes || c.reviewedBy === 'human');
    const hasCompletionEvidence = /photo|report|completion|sign-?off/i.test([
        c.scopeDetails,
        c.sysNote,
        c.manualNotes,
    ].filter(Boolean).join(' '));
    const hasXeroId = Boolean(c.xeroInvoiceId);
    const hasXeroExportSource = /^xero-/.test(cleanString(c.source?.profileId));
    const hasStaticInvoice = Boolean(c.invoiceNumber || c.source?.filename);
    const hasXeroInvoiceFacts = hasXeroId || hasXeroExportSource;

    return [
        {
            id: 'xero',
            label: 'Xero invoice',
            source: hasXeroInvoiceFacts ? 'Xero export' : 'Static import',
            percent: hasXeroId ? 90 : hasXeroExportSource ? 78 : hasStaticInvoice ? 55 : 0,
            state: hasXeroId ? 'xero-id-present' : hasXeroExportSource ? 'xero-export' : hasStaticInvoice ? 'static-import' : 'placeholder',
            tone: hasXeroInvoiceFacts ? 'green' : 'amber',
            detail: hasXeroId
                ? 'Xero invoice identifier is present for read-only reconciliation.'
                : hasXeroExportSource
                    ? 'Imported from a Xero export; OAuth sync is not required for this pass.'
                    : 'Using imported register facts until a Xero export or optional OAuth sync is added.',
            store: 'receivable_cases',
        },
        {
            id: 'quotemaker',
            label: 'QuoteMaker project',
            source: 'QuoteMaker',
            percent: bestProjectScore,
            state: bestProjectScore >= 70 ? 'matched' : bestProjectScore > 0 ? 'weak-match' : 'placeholder',
            tone: bestProjectScore >= 70 ? 'green' : bestProjectScore > 0 ? 'amber' : 'red',
            detail: bestProjectScore > 0
                ? `Best project match is ${bestProjectScore}% confidence from ${bestProjectMatch.match.reasons.join(', ') || 'text overlap'}.`
                : 'No saved project has been linked yet.',
            store: 'receivable_project_links',
        },
        {
            id: 'email',
            label: 'Email thread',
            source: 'Email',
            percent: hasEmailEvidence ? Math.min(92, 55 + (emailEvidenceCount * 9)) : 0,
            state: hasEmailEvidence ? 'evidence-present' : 'placeholder',
            tone: hasEmailEvidence ? 'green' : 'red',
            detail: hasEmailEvidence
                ? emailEvidenceCount
                    ? `${emailEvidenceCount} imported email evidence item${emailEvidenceCount === 1 ? '' : 's'} can be cited before any contact recommendation.`
                    : 'Imported correspondence can be cited before any contact recommendation.'
                : 'No email thread has been ingested yet; assistant must say this is missing.',
            store: 'receivable_evidence_items',
        },
        {
            id: 'completion',
            label: 'Completion evidence',
            source: 'Files / notes',
            percent: hasCompletionEvidence ? 58 : hasManualReview ? 35 : 0,
            state: hasCompletionEvidence ? 'partial' : hasManualReview ? 'manual-note-only' : 'placeholder',
            tone: hasCompletionEvidence ? 'amber' : hasManualReview ? 'amber' : 'red',
            detail: hasCompletionEvidence
                ? 'Completion-related text exists, but attachments/photos still need first-class evidence rows.'
                : hasManualReview
                    ? 'Manual notes exist, but completion files are not linked yet.'
                    : 'No completion photos, report, or final sign-off evidence has been linked.',
            store: 'receivable_evidence_items',
        },
    ];
}

function tokenise(value) {
    return cleanString(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .split(/\s+/)
        .filter(t => t.length >= 3);
}

function textContainsAll(target, source) {
    const tokens = tokenise(source);
    if (tokens.length === 0) return false;
    const targetText = ` ${tokenise(target).join(' ')} `;
    return tokens.every(t => targetText.includes(` ${t} `));
}

export function scoreReceivablesProjectMatch(caseRecord = {}, project = {}) {
    const c = normalizeReceivablesCase(caseRecord, { keepRaw: false });
    const projectText = [
        project.title,
        project.quoteNumber,
        project.address,
        project.formData?.address,
        project.formData?.strataplan,
        project.formData?.orderNumber,
        project.formData?.clientCompany,
        project.formData?.clientName,
        project.quoteData?.meta?.quoteNumber,
        project.quoteData?.header?.title,
    ].filter(Boolean).join(' ');

    const reasons = [];
    let score = 0;

    const exactish = (value) => value && projectText.toLowerCase().includes(String(value).toLowerCase());
    if (c.invoiceNumber && exactish(c.invoiceNumber)) {
        score += 35;
        reasons.push('invoice number');
    }
    if (c.workOrder && exactish(c.workOrder)) {
        score += 30;
        reasons.push('work order');
    }
    if (c.quoteRef && exactish(c.quoteRef)) {
        score += 30;
        reasons.push('quote reference');
    }
    if (c.sp && exactish(c.sp.replace(/^SP\s*/i, ''))) {
        score += 20;
        reasons.push('strata plan');
    }
    if (c.propertyAddress && textContainsAll(projectText, c.propertyAddress)) {
        score += 20;
        reasons.push('site address');
    }
    if (c.company && exactish(c.company)) {
        score += 10;
        reasons.push('requester company');
    }

    const capped = Math.min(100, score);
    return {
        score: capped,
        confidence: capped >= 70 ? 'high' : capped >= 40 ? 'medium' : capped > 0 ? 'low' : 'none',
        reasons,
    };
}

export function isReceivablesIntelligenceDisabled() {
    try {
        if (typeof window !== 'undefined' && window[RECEIVABLES_KILL_SWITCH]) return true;
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem(RECEIVABLES_KILL_SWITCH) === 'true';
        }
    } catch {
        return false;
    }
    return false;
}

export function formatCurrency(value) {
    const amount = Number(value) || 0;
    return amount.toLocaleString('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
    });
}

async function getReceivablesDataStore() {
    const { DataService } = await import('./data-service');
    if (!DataService?.receivables) {
        throw new Error('receivable_cases store unavailable');
    }
    return DataService.receivables;
}

export async function listReceivableCases() {
    const store = await getReceivablesDataStore();
    const rows = await store.getAll();
    return rows.map(row => normalizeReceivablesCase(row, { keepRaw: false })).sort(compareCasePriority);
}

export async function saveReceivableCases(cases = []) {
    const store = await getReceivablesDataStore();
    const rows = cases.map(c => normalizeReceivablesCase(c, { keepRaw: false }));
    await store.bulkSave(rows);
    return rows;
}

export async function importReceivableCasesFromText(text, filename) {
    const existing = await listReceivableCases();
    const importedCases = parseReceivablesImport(text, filename);
    const result = mergeReceivablesCases(existing, importedCases);
    await saveReceivableCases(result.cases);
    return result;
}

export async function updateReceivableCase(id, patch = {}) {
    if (!id) return null;
    const store = await getReceivablesDataStore();
    const existing = await store.getById(id);
    if (!existing) return null;
    const next = normalizeReceivablesCase({
        ...existing,
        ...patch,
        reviewedBy: patch.reviewedBy || existing.reviewedBy || 'human',
        lastReviewedAt: patch.lastReviewedAt || new Date().toISOString(),
        updated_at: new Date().toISOString(),
    }, { keepRaw: false });
    await store.save(next);
    return next;
}

export async function clearReceivableCases() {
    const store = await getReceivablesDataStore();
    await store.clear();
}
